import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '../components/ui/dialog';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { 
  Users, 
  UserCog, 
  MapPin, 
  Phone, 
  Mail, 
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  FileText,
  ArrowRightLeft,
  Shield,
  Building2,
  Search,
  Pencil,
  Upload
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Rol etiketleri
const ROLE_LABELS = {
  super_admin: 'Süper Admin',
  admin: 'Admin',
  hesap_yoneticisi: 'Hesap Yöneticisi',
  yardimci: 'Yardımcı',
  bireysel_hesap: 'Bireysel',
  site_sakini: 'Site Sakini',
  sofor: 'Şoför',
  calisan: 'Çalışan',
  ziyaretci: 'Ziyaretçi'
};

const ManagementPage = () => {
  const { user } = useAuth();
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [siteSearchTerm, setSiteSearchTerm] = useState('');
  const [managers, setManagers] = useState([]);
  const [assistants, setAssistants] = useState([]);
  const [locations, setLocations] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [history, setHistory] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showManagerChangeModal, setShowManagerChangeModal] = useState(false);
  const [showAssistantModal, setShowAssistantModal] = useState(false);
  const [showDecisionModal, setShowDecisionModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [newManagerId, setNewManagerId] = useState('');
  const [newAssistantId, setNewAssistantId] = useState('');
  const [changeNotes, setChangeNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  
  // Decision form
  const [decisionForm, setDecisionForm] = useState({
    title: '',
    decision_number: '',
    decision_date: '',
    description: '',
    participants: ''
  });
  const [decisionFile, setDecisionFile] = useState(null);
  const [editingDecision, setEditingDecision] = useState(null);
  const decisionFileRef = useRef(null);

  const fetchSites = useCallback(async () => {
    try {
      const token = localStorage.getItem('hims_token');
      console.log('[ManagementPage] Fetching sites from:', `${API_URL}/api/sites`);
      const res = await fetch(`${API_URL}/api/sites`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      console.log('[ManagementPage] Sites response status:', res.status);
      if (res.ok) {
        const data = await res.json();
        setSites(data);
        if (data.length > 0 && !selectedSite) {
          setSelectedSite(data[0]);
        }
      } else {
        console.error('[ManagementPage] Sites fetch failed:', res.status, res.statusText);
        setLoading(false);
      }
    } catch (error) {
      console.error('[ManagementPage] Sites fetch error:', error);
      setLoading(false);
    }
  }, [selectedSite]);

  const fetchSiteDetails = useCallback(async (site) => {
    if (!site) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('hims_token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Fetch locations for this site
      console.log('[ManagementPage] Fetching locations for site:', site.id);
      const locRes = await fetch(`${API_URL}/api/accounts/${site.id}/locations`, { headers });
      console.log('[ManagementPage] Locations response status:', locRes.status);
      if (locRes.ok) {
        const locData = await locRes.json();
        setLocations(locData);
      } else {
        console.error('[ManagementPage] Locations fetch failed:', locRes.status);
      }
      
      // Fetch managers from site.manager_ids
      const managersList = [];
      const siteManagerIds = site.manager_ids || [];
      for (const managerId of siteManagerIds) {
        try {
          const userRes = await fetch(`${API_URL}/api/users/${managerId}`, { headers });
          if (userRes.ok) {
            const userData = await userRes.json();
            managersList.push(userData);
          }
        } catch (e) {
          console.error('Error fetching manager:', e);
        }
      }
      setManagers(managersList);
      
      // Fetch assistants from site.assistant_ids
      const assistantsList = [];
      const siteAssistantIds = site.assistant_ids || [];
      for (const assistantId of siteAssistantIds) {
        try {
          const userRes = await fetch(`${API_URL}/api/users/${assistantId}`, { headers });
          if (userRes.ok) {
            const userData = await userRes.json();
            assistantsList.push(userData);
          }
        } catch (e) {
          console.error('Error fetching assistant:', e);
        }
      }
      setAssistants(assistantsList);
      
      // Fetch ALL users for dropdown selections
      const allUsersRes = await fetch(`${API_URL}/api/users`, { headers });
      if (allUsersRes.ok) {
        const allUsersData = await allUsersRes.json();
        setAllUsers(allUsersData);
      }
      
      // Fetch management history
      const histRes = await fetch(`${API_URL}/api/management/history/${site.id}`, { headers });
      if (histRes.ok) {
        const histData = await histRes.json();
        setHistory(histData);
      }
      
      // Fetch decisions (Karar Defteri)
      const decRes = await fetch(`${API_URL}/api/sites/${site.id}/decisions`, { headers });
      if (decRes.ok) {
        const decData = await decRes.json();
        setDecisions(decData);
      }
      
    } catch (error) {
      console.error('Site details fetch error:', error);
      toast.error('Bilgiler yüklenemedi. Lütfen sayfayı yenileyin');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  useEffect(() => {
    if (selectedSite) {
      fetchSiteDetails(selectedSite);
    }
  }, [selectedSite, fetchSiteDetails]);

  const handleManagerChange = async () => {
    if (!newManagerId || !selectedSite) {
      toast.error('Lütfen yeni yönetici seçin');
      return;
    }
    
    setProcessing(true);
    try {
      const token = localStorage.getItem('hims_token');
      const res = await fetch(`${API_URL}/api/management/change`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          site_id: selectedSite.id,
          new_manager_id: newManagerId,
          notes: changeNotes
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success('Yönetici değişikliği başarılı!');
        setShowManagerChangeModal(false);
        setNewManagerId('');
        setChangeNotes('');
        
        // Refresh site data from API
        const siteRes = await fetch(`${API_URL}/api/sites/${selectedSite.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (siteRes.ok) {
          const freshSite = await siteRes.json();
          setSelectedSite(freshSite);
          fetchSiteDetails(freshSite);
        }
        
        // PDF otomatik açılmasın - sadece Görüntüle butonuyla açılsın
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Yönetici değişikliği yapılamadı');
      }
    } catch (error) {
      console.error('Manager change error:', error);
      toast.error('Sunucuya bağlanılamadı. İnternet bağlantınızı kontrol edin');
    } finally {
      setProcessing(false);
    }
  };

  const handleAssistantAssign = async () => {
    if (!newAssistantId || !selectedSite) {
      toast.error('Lütfen bir yardımcı seçin');
      return;
    }
    
    setProcessing(true);
    try {
      const token = localStorage.getItem('hims_token');
      
      // Site'ye yardımcı ekle (site.assistant_ids array'ine ekle)
      const res = await fetch(`${API_URL}/api/sites/${selectedSite.id}/assistants`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          assistant_id: newAssistantId
        })
      });
      
      if (res.ok) {
        toast.success('Yardımcı eklendi!');
        setShowAssistantModal(false);
        setNewAssistantId('');
        
        // Refresh site data from API
        const siteRes = await fetch(`${API_URL}/api/sites/${selectedSite.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (siteRes.ok) {
          const freshSite = await siteRes.json();
          setSelectedSite(freshSite);
          fetchSiteDetails(freshSite);
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Yardımcı atanamadı');
      }
    } catch (error) {
      console.error('Assistant assign error:', error);
      toast.error('Sunucuya bağlanılamadı');
    } finally {
      setProcessing(false);
    }
  };

  const openDocument = async (historyId) => {
    try {
      const token = localStorage.getItem('hims_token');
      const response = await fetch(`${API_URL}/api/management/document/${historyId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        toast.error('Belge yüklenemedi. Lütfen tekrar deneyin');
        return;
      }
      
      // HTML içeriğini al ve yeni sekmede aç (yazdırılabilir)
      const html = await response.text();
      const newWindow = window.open('', '_blank');
      newWindow.document.write(html);
      newWindow.document.close();
      
      toast.success('Belge açıldı - Yazdırmak için Ctrl+P');
    } catch (error) {
      console.error('Document error:', error);
      toast.error('Belge açılırken bir sorun oluştu');
    }
  };

  // Karar Ekleme/Düzenleme
  const handleDecisionSubmit = async () => {
    console.log('[ManagementPage] Karar ekleniyor...', { selectedSite: selectedSite?.id, form: decisionForm });
    
    if (!decisionForm.title) {
      toast.error('Lütfen karar başlığı girin');
      return;
    }
    
    if (!selectedSite) {
      toast.error('Lütfen önce bir site seçin');
      return;
    }
    
    setProcessing(true);
    try {
      const token = localStorage.getItem('hims_token');
      if (!token) {
        toast.error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
        setProcessing(false);
        return;
      }
      
      const formData = new FormData();
      formData.append('title', decisionForm.title);
      if (decisionForm.decision_number) formData.append('decision_number', decisionForm.decision_number);
      if (decisionForm.decision_date) formData.append('decision_date', decisionForm.decision_date);
      if (decisionForm.description) formData.append('description', decisionForm.description);
      if (decisionForm.participants) formData.append('participants', decisionForm.participants);
      if (decisionFile) {
        console.log('[ManagementPage] Uploading file:', decisionFile.name, decisionFile.type);
        formData.append('document', decisionFile);
      }
      
      const isEditing = !!editingDecision;
      const url = isEditing 
        ? `${API_URL}/api/decisions/${editingDecision.id}`
        : `${API_URL}/api/sites/${selectedSite.id}/decisions`;
      
      console.log('[ManagementPage] Submitting decision to:', url, 'Method:', isEditing ? 'PUT' : 'POST');
      
      const res = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      console.log('[ManagementPage] Decision submit response:', res.status);
      
      if (res.ok) {
        toast.success(isEditing ? 'Karar güncellendi!' : 'Karar eklendi!');
        setShowDecisionModal(false);
        setDecisionForm({ title: '', decision_number: '', decision_date: '', description: '', participants: '' });
        setDecisionFile(null);
        setEditingDecision(null);
        fetchSiteDetails(selectedSite);
      } else {
        let errorMessage = 'İşlem başarısız. Lütfen tekrar deneyin';
        try {
          const error = await res.json();
          console.error('[ManagementPage] Decision submit error:', error);
          errorMessage = error.detail || errorMessage;
        } catch (e) {
          console.error('[ManagementPage] Could not parse error response');
        }
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('[ManagementPage] Decision submit exception:', error);
      toast.error('Sunucuya bağlanılamadı: ' + (error.message || 'Bilinmeyen hata'));
    } finally {
      setProcessing(false);
    }
  };

  // Karar Düzenleme Modal Aç
  const openEditDecision = (decision) => {
    setEditingDecision(decision);
    setDecisionForm({
      title: decision.title || '',
      decision_number: decision.decision_number || '',
      decision_date: decision.decision_date ? decision.decision_date.split('T')[0] : '',
      description: decision.description || '',
      participants: decision.participants || ''
    });
    setDecisionFile(null);
    setShowDecisionModal(true);
  };

  // Karar Belgesi İndir
  const downloadDecisionDocument = async (decisionId, decisionNumber) => {
    try {
      const token = localStorage.getItem('hims_token');
      const response = await fetch(`${API_URL}/api/decisions/${decisionId}/document`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        toast.error('Belge indirilemedi');
        return;
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `karar_${decisionNumber || decisionId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Belge indirildi');
    } catch (error) {
      toast.error('İndirme hatası');
    }
  };

  // Karar Sil
  const deleteDecision = async (decisionId) => {
    if (!window.confirm('Bu kararı silmek istediğinize emin misiniz?')) return;
    
    try {
      const token = localStorage.getItem('hims_token');
      const res = await fetch(`${API_URL}/api/decisions/${decisionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Karar silindi');
        fetchSiteDetails(selectedSite);
      }
    } catch (error) {
      toast.error('Silme hatası');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const VerificationBadge = ({ verified, type }) => {
    if (verified) {
      return (
        <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/30 text-xs">
          <CheckCircle className="h-3 w-3 mr-1" />
          Doğrulandı
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="bg-red-500/10 text-red-400 border-red-500/30 text-xs">
        <XCircle className="h-3 w-3 mr-1" />
        Doğrulanmadı
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            Yönetim Paneli
          </h1>
          <p className="text-muted-foreground">
            Hesap yöneticileri ve yardımcıları yönetin
          </p>
        </div>
        
        {/* Site Selector with Search */}
        <div className="flex items-center gap-3">
          <Select 
            value={selectedSite?.id || ''} 
            onValueChange={async (val) => {
              // Site verilerini API'den güncel olarak al
              const token = localStorage.getItem('hims_token');
              try {
                const res = await fetch(`${API_URL}/api/sites/${val}`, {
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                  const freshSite = await res.json();
                  setSelectedSite(freshSite);
                } else {
                  const site = sites.find(s => s.id === val);
                  setSelectedSite(site);
                }
              } catch (e) {
                const site = sites.find(s => s.id === val);
                setSelectedSite(site);
              }
              setSiteSearchTerm('');
            }}
          >
            <SelectTrigger className="w-[300px]" data-testid="site-selector">
              <Building2 className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Site seçin veya arayın..." />
            </SelectTrigger>
            <SelectContent>
              {/* Arama kutusu dropdown içinde */}
              <div className="p-2 border-b">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Site ara..."
                    value={siteSearchTerm}
                    onChange={(e) => setSiteSearchTerm(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
              {/* Site listesi */}
              <div className="max-h-[200px] overflow-y-auto">
                {sites
                  .filter(site => !siteSearchTerm || site.name.toLowerCase().includes(siteSearchTerm.toLowerCase()))
                  .length > 0 ? (
                    sites
                      .filter(site => !siteSearchTerm || site.name.toLowerCase().includes(siteSearchTerm.toLowerCase()))
                      .map((site) => (
                        <SelectItem key={site.id} value={site.id}>
                          {site.name}
                        </SelectItem>
                      ))
                  ) : (
                    <div className="p-3 text-sm text-muted-foreground text-center">
                      "{siteSearchTerm}" ile eşleşen site bulunamadı
                    </div>
                  )}
              </div>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <RefreshCw className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : selectedSite ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Mevcut Yönetici Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <UserCog className="h-5 w-5 text-blue-500" />
                  Mevcut Yönetici{managers.length > 1 ? 'ler' : ''}
                </span>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setShowManagerChangeModal(true)}
                  data-testid="change-manager-btn"
                >
                  <ArrowRightLeft className="h-4 w-4 mr-1" />
                  Değiştir
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {managers.length > 0 ? (
                <div className="space-y-3">
                  {managers.map((manager, index) => (
                    <div 
                      key={manager.id}
                      className={`p-4 rounded-lg border ${index === 0 ? 'bg-blue-500/10 border-blue-500/30' : 'bg-accent/30 border-border'}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-semibold text-lg">{manager.full_name}</p>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                            <Mail className="h-4 w-4" />
                            {manager.email}
                          </div>
                          {manager.phone && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                              <Phone className="h-4 w-4" />
                              {manager.phone}
                            </div>
                          )}
                        </div>
                        <Badge className={index === 0 ? 'bg-blue-500' : 'bg-slate-500'}>
                          {index === 0 ? 'Ana Yönetici' : 'Yönetici'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-yellow-500" />
                  <p>Bu hesap için yönetici atanmamış</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Yardımcılar Card */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="h-5 w-5 text-green-500" />
                  Yardımcılar ({assistants.length}/3)
                </CardTitle>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => {
                    setNewAssistantId('');
                    setSelectedLocation(null);
                    setShowAssistantModal(true);
                  }}
                  disabled={assistants.length >= 3}
                  data-testid="add-assistant-btn"
                >
                  + Yardımcı Ekle
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {assistants.length > 0 ? (
                <div className="space-y-3">
                  {assistants.map((asst, idx) => (
                    <div 
                      key={asst.id}
                      className="p-3 rounded-lg bg-accent/30 border border-border"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{asst.full_name}</span>
                            <Badge variant="outline" className="text-xs bg-green-500/10 text-green-400 border-green-500/30">
                              Yardımcı
                            </Badge>
                          </div>
                          <div className="flex flex-wrap gap-3 mt-2 text-sm text-muted-foreground">
                            {asst.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" /> {asst.phone}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" /> {asst.email}
                            </span>
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="text-red-400 hover:text-red-500"
                          onClick={async () => {
                            if (window.confirm(`${asst.full_name} yardımcısını kaldırmak istediğinize emin misiniz?`)) {
                              try {
                                const token = localStorage.getItem('hims_token');
                                const res = await fetch(`${API_URL}/api/sites/${selectedSite.id}/assistants/${asst.id}`, {
                                  method: 'DELETE',
                                  headers: { 'Authorization': `Bearer ${token}` }
                                });
                                if (res.ok) {
                                  toast.success('Yardımcı kaldırıldı');
                                  // Refresh site data
                                  const siteRes = await fetch(`${API_URL}/api/sites/${selectedSite.id}`, {
                                    headers: { 'Authorization': `Bearer ${token}` }
                                  });
                                  if (siteRes.ok) {
                                    const updatedSite = await siteRes.json();
                                    setSelectedSite(updatedSite);
                                    fetchSiteDetails(updatedSite);
                                  }
                                }
                              } catch (error) {
                                toast.error('Hata oluştu');
                              }
                            }
                          }}
                        >
                          Kaldır
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Henüz yardımcı atanmamış</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Karar Defteri - Yönetim Kararları */}
          <Card className="lg:col-span-2">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5 text-orange-500" />
                  Karar Defteri
                </CardTitle>
                <Button 
                  size="sm"
                  onClick={() => {
                    setEditingDecision(null);
                    setDecisionForm({ title: '', decision_number: '', decision_date: '', description: '', participants: '' });
                    setDecisionFile(null);
                    setShowDecisionModal(true);
                  }}
                  data-testid="add-decision-btn"
                >
                  + Karar Ekle
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {decisions.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left p-3 text-sm text-muted-foreground">No</th>
                        <th className="text-left p-3 text-sm text-muted-foreground">Tarih</th>
                        <th className="text-left p-3 text-sm text-muted-foreground">Başlık</th>
                        <th className="text-left p-3 text-sm text-muted-foreground">Katılımcılar</th>
                        <th className="text-left p-3 text-sm text-muted-foreground">Ekleyen</th>
                        <th className="text-left p-3 text-sm text-muted-foreground">Belge</th>
                        <th className="text-left p-3 text-sm text-muted-foreground">İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {decisions.map((dec) => (
                        <tr key={dec.id} className="border-b border-border/50 hover:bg-accent/30">
                          <td className="p-3 text-sm font-medium">{dec.decision_number || '-'}</td>
                          <td className="p-3 text-sm">{formatDate(dec.decision_date)}</td>
                          <td className="p-3 text-sm">
                            <div className="max-w-xs">
                              <span className="font-medium">{dec.title}</span>
                              {dec.description && (
                                <p className="text-xs text-muted-foreground truncate">{dec.description}</p>
                              )}
                            </div>
                          </td>
                          <td className="p-3 text-sm text-muted-foreground">{dec.participants || '-'}</td>
                          <td className="p-3 text-sm text-muted-foreground">{dec.created_by_name}</td>
                          <td className="p-3">
                            {dec.document_url ? (
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => downloadDecisionDocument(dec.id, dec.decision_number)}
                              >
                                <FileText className="h-4 w-4 mr-1" />
                                İndir
                              </Button>
                            ) : (
                              <span className="text-xs text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-1">
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="text-blue-400 hover:text-blue-500"
                                onClick={() => openEditDecision(dec)}
                                title="Düzenle"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="text-red-400 hover:text-red-500"
                                onClick={() => deleteDecision(dec.id)}
                                title="Sil"
                              >
                                Sil
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Henüz karar eklenmemiş</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Yönetici Değişiklik Geçmişi */}
          <Card className="lg:col-span-2">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <ArrowRightLeft className="h-5 w-5 text-purple-500" />
                Yönetici Değişiklik Geçmişi
              </CardTitle>
            </CardHeader>
            <CardContent>
              {history.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left p-3 text-sm text-muted-foreground">Tarih</th>
                        <th className="text-left p-3 text-sm text-muted-foreground">Eski Yönetici</th>
                        <th className="text-left p-3 text-sm text-muted-foreground">Yeni Yönetici</th>
                        <th className="text-left p-3 text-sm text-muted-foreground">Onaylayan</th>
                        <th className="text-right p-3 text-sm text-muted-foreground">Döküman</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((h) => (
                        <tr key={h.id} className="border-b border-border/50 hover:bg-accent/20">
                          <td className="p-3 text-sm">{formatDate(h.created_at)}</td>
                          <td className="p-3 text-sm">
                            <span className="text-red-400">{h.old_manager_name || '-'}</span>
                          </td>
                          <td className="p-3 text-sm">
                            <span className="text-green-400">{h.new_manager_name}</span>
                          </td>
                          <td className="p-3 text-sm">{h.approved_by_name}</td>
                          <td className="p-3 text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openDocument(h.id)}
                            >
                              <FileText className="h-4 w-4 mr-1" />
                              Görüntüle
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Henüz yönetim değişikliği yapılmamış</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Lütfen bir hesap seçin</p>
          </CardContent>
        </Card>
      )}

      {/* Manager Change Modal */}
      <Dialog open={showManagerChangeModal} onOpenChange={setShowManagerChangeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Yönetici Değiştir</DialogTitle>
            <DialogDescription>
              {selectedSite?.name} hesabının yöneticisini değiştirin. 
              Eski yönetici Site Sakini rolüne düşürülecektir.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Yeni Yönetici</label>
              <Select value={newManagerId} onValueChange={setNewManagerId}>
                <SelectTrigger data-testid="new-manager-select">
                  <SelectValue placeholder="Kullanıcı seçin" />
                </SelectTrigger>
                <SelectContent>
                  {allUsers
                    .filter(u => u.role !== 'super_admin' && !managers.some(m => m.id === u.id))
                    .map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.full_name} - {ROLE_LABELS[u.role] || u.role}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Notlar (Opsiyonel)</label>
              <Textarea
                value={changeNotes}
                onChange={(e) => setChangeNotes(e.target.value)}
                placeholder="Değişiklik sebebi veya notlar..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManagerChangeModal(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleManagerChange} 
              disabled={!newManagerId || processing}
              data-testid="confirm-manager-change"
            >
              {processing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
              Değişikliği Onayla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assistant Assignment Modal */}
      <Dialog open={showAssistantModal} onOpenChange={setShowAssistantModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Site Yardımcısı Ekle</DialogTitle>
            <DialogDescription>
              Bu siteye yardımcı atayın. Maksimum 3 yardımcı atanabilir.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Yardımcı Seçin</label>
              <Select value={newAssistantId} onValueChange={setNewAssistantId}>
                <SelectTrigger>
                  <SelectValue placeholder="Kullanıcı seçin" />
                </SelectTrigger>
                <SelectContent>
                  {allUsers
                    .filter(u => 
                      u.role !== 'super_admin' && 
                      u.role !== 'admin' && 
                      !managers.some(m => m.id === u.id) &&
                      !assistants.some(a => a.id === u.id)
                    )
                    .map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.full_name} - {ROLE_LABELS[u.role] || u.role}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-2">
                Seçilen kullanıcı bu sitenin yardımcısı olarak atanacaktır.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowAssistantModal(false);
              setNewAssistantId('');
            }}>
              İptal
            </Button>
            <Button 
              onClick={handleAssistantAssign} 
              disabled={processing || !newAssistantId}
            >
              {processing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
              Yardımcı Ekle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decision Modal - Karar Ekle/Düzenle */}
      <Dialog open={showDecisionModal} onOpenChange={(open) => {
        if (!open) {
          setEditingDecision(null);
        }
        setShowDecisionModal(open);
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingDecision ? 'Karar Düzenle' : 'Yeni Karar Ekle'}</DialogTitle>
            <DialogDescription>
              {editingDecision ? 'Karar bilgilerini güncelleyin.' : 'Karar defterine yeni bir karar ekleyin. PDF veya resim belgesi yükleyebilirsiniz.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Karar No</label>
                <Input 
                  placeholder="örn: 2026/01"
                  value={decisionForm.decision_number}
                  onChange={(e) => setDecisionForm({...decisionForm, decision_number: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Karar Tarihi</label>
                <Input 
                  type="date"
                  value={decisionForm.decision_date}
                  onChange={(e) => setDecisionForm({...decisionForm, decision_date: e.target.value})}
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Karar Başlığı *</label>
              <Input 
                placeholder="örn: Şarj ücreti güncelleme kararı"
                value={decisionForm.title}
                onChange={(e) => setDecisionForm({...decisionForm, title: e.target.value})}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Açıklama</label>
              <textarea 
                className="w-full min-h-[80px] p-3 rounded-md border border-input bg-background text-sm"
                placeholder="Karar detayları..."
                value={decisionForm.description}
                onChange={(e) => setDecisionForm({...decisionForm, description: e.target.value})}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Katılımcılar</label>
              <Input 
                placeholder="örn: Ahmet Yılmaz, Mehmet Demir"
                value={decisionForm.participants}
                onChange={(e) => setDecisionForm({...decisionForm, participants: e.target.value})}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Belge Yükle (PDF/Resim)</label>
              <div className="flex items-center gap-2">
                <input
                  ref={decisionFileRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    console.log('[ManagementPage] File selected:', file?.name, file?.type, file?.size);
                    
                    // 10MB limit
                    const maxSize = 10 * 1024 * 1024;
                    if (file && file.size > maxSize) {
                      toast.error(`Dosya çok büyük (${(file.size / 1024 / 1024).toFixed(1)}MB). Maksimum 10MB yüklenebilir.`);
                      e.target.value = '';
                      setDecisionFile(null);
                      return;
                    }
                    
                    setDecisionFile(file || null);
                  }}
                  className="hidden"
                  id="decision-file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => decisionFileRef.current?.click()}
                  className="flex-1"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {decisionFile ? 'Dosya Değiştir' : 'Dosya Seç'}
                </Button>
              </div>
              {decisionFile && (
                <p className="text-xs text-green-600 mt-2">
                  ✓ Seçilen: {decisionFile.name} ({(decisionFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
              {editingDecision?.document_url && !decisionFile && (
                <p className="text-xs text-muted-foreground mt-2">
                  Mevcut belge var. Yeni dosya seçerseniz değiştirilecektir.
                </p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDecisionModal(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleDecisionSubmit} 
              disabled={processing || !decisionForm.title}
            >
              {processing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingDecision ? 'Güncelle' : 'Karar Ekle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManagementPage;
